/*
 * Wellness Questionnaire - scoring engine.
 *
 * Pure functions, no DOM. Loaded as a plain <script> in the theme (exposes
 * window.WellnessQuizScore) and require()'d by scripts/validate_wellness_quiz.mjs
 * so the browser and the regression test score identically.
 *
 * Prices are always in CENTS (integers), matching Shopify's Liquid `product.price`.
 * Nothing here stores a product ID or a price - both come from the live catalog
 * blob the section renders. That is the whole point: the old QuizKit build pinned
 * numeric IDs, the catalog was recreated, and 92% of shoppers hit a dead end.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.WellnessQuizScore = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function isUsable(entry, map) {
    if (!entry) return false;
    if (entry.available === false) return false;
    var excludedHandles = map.excludedHandles || [];
    if (excludedHandles.indexOf(entry.handle) !== -1) return false;
    var excludeTags = map.excludeTags || [];
    var tags = entry.tags || [];
    for (var i = 0; i < excludeTags.length; i++) {
      for (var j = 0; j < tags.length; j++) {
        if (String(tags[j]).toLowerCase() === String(excludeTags[i]).toLowerCase()) return false;
      }
    }
    return true;
  }

  function answersById(map) {
    var out = {};
    map.questions.forEach(function (q) {
      q.answers.forEach(function (a) {
        out[q.id + '::' + a.id] = a;
      });
    });
    return out;
  }

  /**
   * @param {object} map      parsed wellness-quiz-map.json
   * @param {object} selections  { questionId: [answerId, ...] }
   * @param {object} catalog  { handle: {handle,title,url,price,image,available,tags} } - prices in cents
   * @returns {object} results
   */
  function score(map, selections, catalog) {
    var settings = map.settings || {};
    var maxSingles = settings.maxSingles || 6;
    var minCoverage = settings.bundleMinCoverage || 3;
    var minDirect = settings.bundleMinDirect || 3;
    var minSavingsPct = settings.minSavingsPctToShow || 0;

    var lookup = answersById(map);
    var productScores = {};
    var bundleScores = {};

    Object.keys(selections || {}).forEach(function (qid) {
      (selections[qid] || []).forEach(function (aid) {
        var answer = lookup[qid + '::' + aid];
        if (!answer) return;
        var products = answer.products || {};
        Object.keys(products).forEach(function (h) {
          productScores[h] = (productScores[h] || 0) + products[h];
        });
        var bundles = answer.bundles || {};
        Object.keys(bundles).forEach(function (b) {
          bundleScores[b] = (bundleScores[b] || 0) + bundles[b];
        });
      });
    });

    // Rank singles. Handles that no longer resolve, are out of stock, or are
    // excluded simply drop out here - they never reach the shopper as an error.
    var ranked = Object.keys(productScores)
      .filter(function (h) { return productScores[h] > 0 && isUsable(catalog[h], map); })
      .sort(function (a, b) {
        if (productScores[b] !== productScores[a]) return productScores[b] - productScores[a];
        return a.localeCompare(b); // stable, deterministic
      });

    var topSingles = ranked.slice(0, maxSingles);

    // Bundle coverage against the shopper's top singles.
    var candidates = [];
    Object.keys(map.bundles || {}).forEach(function (handle) {
      var entry = catalog[handle];
      if (!isUsable(entry, map)) return;

      var components = (map.bundles[handle].components || []).filter(function (c) {
        return isUsable(catalog[c], map);
      });
      if (!components.length) return;

      var covered = components.filter(function (c) { return topSingles.indexOf(c) !== -1; });
      var direct = bundleScores[handle] || 0;

      // A bundle headlines only on a deliberate match (an answer that points
      // straight at it) or on broad coverage of what we already recommended.
      // A single weak breadth signal is not enough - that produced pitches like
      // "this $138 kit covers 1 of your 5 formulas".
      if (direct < minDirect && covered.length < minCoverage) return;

      var componentTotal = components.reduce(function (sum, c) { return sum + catalog[c].price; }, 0);
      var savings = componentTotal - entry.price;
      var savingsPct = componentTotal > 0 ? Math.round((savings / componentTotal) * 100) : 0;

      candidates.push({
        handle: handle,
        product: entry,
        components: components,
        covered: covered,
        coverage: covered.length,
        directScore: direct,
        totalScore: direct + covered.length,
        componentTotal: componentTotal,
        savings: savings,
        savingsPct: savingsPct,
        showSavings: savings > 0 && savingsPct >= minSavingsPct
      });
    });

    candidates.sort(function (a, b) {
      if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore;
      if (b.coverage !== a.coverage) return b.coverage - a.coverage;
      if (b.savings !== a.savings) return b.savings - a.savings;
      return a.handle.localeCompare(b.handle);
    });

    var headline = candidates.length ? candidates[0] : null;

    // Never show an empty results page. This is the exact failure the old build shipped.
    var usedFallback = false;
    if (!headline && !topSingles.length) {
      usedFallback = true;
      topSingles = (map.fallback || []).filter(function (h) { return isUsable(catalog[h], map); });
    }

    var coveredSet = {};
    if (headline) headline.covered.forEach(function (c) { coveredSet[c] = true; });

    return {
      headline: headline,
      singles: topSingles.map(function (h) {
        return {
          handle: h,
          product: catalog[h],
          score: productScores[h] || 0,
          inHeadlineBundle: !!coveredSet[h]
        };
      }),
      usedFallback: usedFallback,
      isEmpty: !headline && !topSingles.length,
      debug: { productScores: productScores, bundleScores: bundleScores, candidates: candidates }
    };
  }

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    return '$' + amount;
  }

  return { score: score, formatMoney: formatMoney, isUsable: isUsable };
});
